import type { ExecutorLocations } from "../../services";

export type ClusterLeaf = {
	id: number;
	lat: number;
	lng: number;
	callSign: string;
	location: ExecutorLocations.Model;
};

export type ClusterNode =
	| {
			kind: "leaf";
			key: string;
			lat: number;
			lng: number;
			leaf: ClusterLeaf;
	  }
	| {
			kind: "cluster";
			key: string;
			lat: number;
			lng: number;
			count: number;
			callSigns: string[];
			leaves: ClusterLeaf[];
	  };

/** Individual cars from this zoom. Below — grid clusters. */
export const CLUSTER_MAX_ZOOM = 15;

/** Keep a band around the viewport so pan-in cars already have a marker. */
export const VIEWPORT_PAD = 0.25;

export function filterLeavesInBounds(
	leaves: ClusterLeaf[],
	west: number,
	south: number,
	east: number,
	north: number,
): ClusterLeaf[] {
	if (leaves.length === 0) return leaves;

	const wrapped = west > east;
	const out: ClusterLeaf[] = [];
	for (let i = 0; i < leaves.length; i += 1) {
		const leaf = leaves[i];
		const inLat = leaf.lat >= south && leaf.lat <= north;
		const inLng = wrapped
			? leaf.lng >= west || leaf.lng <= east
			: leaf.lng >= west && leaf.lng <= east;
		if (inLat && inLng) {
			out.push(leaf);
		}
	}
	return out;
}

/**
 * Grid clustering over the given leaves (already viewport-filtered).
 * Cell size shrinks as zoom grows; at CLUSTER_MAX_ZOOM+ returns leaves only.
 */
export function clusterExecutorPoints(
	leaves: ClusterLeaf[],
	zoom: number,
): ClusterNode[] {
	if (leaves.length === 0) return [];
	if (zoom >= CLUSTER_MAX_ZOOM) {
		return leaves.map((leaf) => ({
			kind: "leaf" as const,
			key: `l-${leaf.id}`,
			lat: leaf.lat,
			lng: leaf.lng,
			leaf,
		}));
	}

	// ~ degrees per cell; tuned so city overview merges cars, street level less so
	const cell = Math.max(0.0015, 0.18 / 2 ** Math.max(0, zoom - 8));
	const buckets = new Map<string, ClusterLeaf[]>();

	for (let i = 0; i < leaves.length; i += 1) {
		const leaf = leaves[i];
		const gx = Math.floor(leaf.lng / cell);
		const gy = Math.floor(leaf.lat / cell);
		const key = `${gx}:${gy}`;
		const bucket = buckets.get(key);
		if (bucket) bucket.push(leaf);
		else buckets.set(key, [leaf]);
	}

	const nodes: ClusterNode[] = [];
	buckets.forEach((bucket, cellKey) => {
		if (bucket.length === 1) {
			const leaf = bucket[0];
			nodes.push({
				kind: "leaf",
				key: `l-${leaf.id}`,
				lat: leaf.lat,
				lng: leaf.lng,
				leaf,
			});
			return;
		}

		let latSum = 0;
		let lngSum = 0;
		const callSigns: string[] = [];
		for (let i = 0; i < bucket.length; i += 1) {
			latSum += bucket[i].lat;
			lngSum += bucket[i].lng;
			if (bucket[i].callSign) callSigns.push(bucket[i].callSign);
		}
		nodes.push({
			kind: "cluster",
			key: `c-${cellKey}`,
			lat: latSum / bucket.length,
			lng: lngSum / bucket.length,
			count: bucket.length,
			callSigns,
			leaves: bucket,
		});
	});

	return nodes;
}
